import React, { useState, useEffect, useRef } from 'react'

import { Autocomplete, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { useNavigate } from 'react-router-dom'

import AppLayout from '../../components/Layouts/AppLayout'
import AddButton from '../../components/AddButton'
import Loading from '../../components/Loading'
import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import Table from '../../components/quotes/table.jsx'
import { useAuth } from '../../hooks/auth'
import { handleAxiosError, isValid } from '../../helpers/helper'
import { buildUrl } from '../../helpers/apiHelper'
import quoteStatusAPI from '../../services/api/Quotes/quoteStatusAPI'

const Quotes = () => {

    const { user } = useAuth({ middleware: 'guest ' })
    const navigate = useNavigate()

    const { choosesite, setChoosesite, company_id, config, pusher } = useStateContext()

    const [page, setPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [total, setTotal] = useState(0)
    const [quotes, setQuotes] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [random, setRandom] = useState(null)

    /* filters - restored from localStorage so they survive leaving/reopening this page */
    const [customers, setCustomers] = useState([])
    const [shipToLocations, setShipToLocations] = useState([])
    const [filterCustomerId, setFilterCustomerId] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.quote_customer_id || ''
        }
        return ''
    })
    const [filterCustomerName, setFilterCustomerName] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.quote_customer_name || ''
        }
        return ''
    })
    const [filterShipToLocationId, setFilterShipToLocationId] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.quote_ship_to_location_id || ''
        }
        return ''
    })
    const [filterQuoteStatus, setFilterQuoteStatus] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.quote_status || ''
        }
        return ''
    })
    const [filterQuoteDate, setFilterQuoteDate] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.quote_date ? dayjs(filter[0].quote_date) : null
        }
        return null
    })
    const [quoteStatuses, setQuoteStatuses] = useState([])
    const isInitialCustomerEffect = useRef(true)

    useEffect(() => {
        setChoosesite(localStorage.getItem('site'))
    }, [])

    useEffect(() => {
        getQuoteStatuses()
    }, [])

    useEffect(() => {
        if (isValid(company_id)) {
            getCustomers(company_id)
        }
    }, [company_id])

    useEffect(() => {
        if (isValid(filterCustomerId)) {
            getShipToLocations(filterCustomerId)
        } else {
            setShipToLocations([])
        }

        if (isInitialCustomerEffect.current) {
            isInitialCustomerEffect.current = false
        } else {
            setFilterShipToLocationId('')
        }
    }, [filterCustomerId])

    /* persist filters to localStorage so they survive leaving/reopening this page */
    const createFilterArray = (customerId, customerName, shipToLocationId, quoteStatus, quoteDate) => {
        if (localStorage.getItem('filters') === null) {
            localStorage.setItem('filters', JSON.stringify([{}]))
        }

        const filters = JSON.parse(localStorage.getItem('filters'))

        filters[0].quote_customer_id = customerId
        filters[0].quote_customer_name = customerName
        filters[0].quote_ship_to_location_id = shipToLocationId
        filters[0].quote_status = quoteStatus
        filters[0].quote_date = quoteDate ? dayjs(quoteDate).format('YYYY-MM-DD') : ''

        localStorage.setItem('filters', JSON.stringify(filters))
    }

    useEffect(() => {
        createFilterArray(filterCustomerId, filterCustomerName, filterShipToLocationId, filterQuoteStatus, filterQuoteDate)
    }, [filterCustomerId, filterCustomerName, filterShipToLocationId, filterQuoteStatus, filterQuoteDate])

    useEffect(() => {
        if (isValid(choosesite)) {
            getQuotes()
        }
    }, [choosesite, page, rowsPerPage, filterCustomerId, filterShipToLocationId, filterQuoteStatus, filterQuoteDate, random])

    useEffect(() => {
        const clientId = localStorage.getItem('client_id')

        const channelCreated = pusher.subscribe(`${clientId}-quote-created`)
        const channelUpdated = pusher.subscribe(`${clientId}-quote-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-quote-deleted`)

        channelCreated.bind(`${clientId}-quote-created-event`, data => {
            setRandom(Math.random())
        })

        channelUpdated.bind(`${clientId}-quote-updated-event`, data => {
            setRandom(Math.random())
        })

        channelDeleted.bind(`${clientId}-quote-deleted-event`, data => {
            setRandom(Math.random())
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-quote-created`)
            pusher.unsubscribe(`${clientId}-quote-updated`)
            pusher.unsubscribe(`${clientId}-quote-deleted`)
        })
    }, [])

    const getQuoteStatuses = async () => {
        await quoteStatusAPI.getQuoteStatuses(config)
            .then((data) => {
                setQuoteStatuses(data)
            })
    }

    const getCustomers = async (id) => {
        await axios
            .get(`/api/customers?company_id=${id}&blocked=0`, config)
            .then(res => {
                setCustomers(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getShipToLocations = async (id) => {
        await axios
            .get(`/api/ship-addresses?customer_id=${id}`, config)
            .then(res => {
                setShipToLocations(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getQuotes = async () => {
        setIsLoading(true)

        const url = buildUrl(
            '/api/quotes',
            {
                where: {
                    'site_id': choosesite,
                    'customer_id': filterCustomerId,
                    'ship_to_location_id': filterShipToLocationId,
                    'quote_status': filterQuoteStatus,
                    'quote_date': filterQuoteDate ? dayjs(filterQuoteDate).format('YYYY-MM-DD') : '',
                    'page': page,
                    'page_size': rowsPerPage
                }
            }
        )

        await axios
            .get(url, config)
            .then(res => {
                setTotal(res.data?.total)
                setQuotes((res.data?.data ?? []).map(quote => ({ ...quote, quote_number: `Q-${quote.id}` })))
            })
            .catch((err) => console.error(err))
            .finally(() => setIsLoading(false))
    }

    /* handlers */

    const handleCustomerChange = (a, b) => {
        setFilterCustomerId(b?.id || '')
        setFilterCustomerName(b?.label || '')
        setPage(1)
    }

    const optionsCustomers = customers?.map(option => ({ label: option.customer_no + ' - ' + option.name, id: option.id }))

    return (
        <>

            {isLoading ? <Loading /> : ''}
            <AppLayout>

                <div className='p-6 flex w-full items-start justify-between gap-5 max-[1830px]:flex-col'>
                    <div className='w-full bg-white rounded-md'>
                        <div className='p-5 flex justify-between items-center border-b border-[#EEEFF2]'>
                            <div className='flex gap-2 items-center'>
                                <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('quotes')}</p>
                            </div>
                            <div className='flex gap-5 items-center'>
                                {['master_admin', 'office_manager'].includes(user?.role) ? (
                                    <Tooltip disableInteractive title={user?.cannot_create ? t('free_limit_exceeded') : t('create_new_quote')} placement='bottom'>
                                        <div>
                                            <AddButton disabled={user?.cannot_create ?? true} onClick={() => navigate('create')}><i className="fa-solid fa-plus text-[#333333]"></i></AddButton>
                                        </div>
                                    </Tooltip>
                                ) : (
                                    ''
                                )}
                            </div>
                        </div>
                        <div className='p-5 border-b border-[#EEEFF2]'>
                            <div className='flex-row flex justify-between items-end w-full gap-3'>
                                <div className='w-full'>
                                    <Autocomplete
                                        disablePortal
                                        id="quotes-filter-customer-autocomplete"
                                        options={optionsCustomers}
                                        value={filterCustomerId ? { label: filterCustomerName, id: filterCustomerId } : null}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        onChange={handleCustomerChange}
                                        sx={{
                                            boxShadow: 'none',
                                            '.MuiOutlinedInput-notchedOutline': {
                                                border: 'none !important'
                                            },
                                            background: '#F5F5F5',
                                            borderRadius: '6px',
                                            "&.Mui-focused .MuiIconButton-root": {
                                                color: 'rgba(0,0,0,.42)'
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('search_by_customer')}
                                                InputLabelProps={{ ...params.InputLabelProps, }}
                                            />
                                        )}
                                    />
                                </div>

                                <div className='w-full'>
                                    <FormControl sx={{ width: 'inherit' }} disabled={!isValid(filterCustomerId)}>
                                        <InputLabel sx={{}}>{t('search_by_ship_to')}</InputLabel>
                                        <Select
                                            value={filterShipToLocationId}
                                            onChange={e => { setFilterShipToLocationId(e.target.value); setPage(1) }}
                                            sx={{
                                                boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: 'none !important' }, background: '#F5F5F5', borderRadius: '6px',
                                                ".MuiSelect-iconOutlined": { display: filterShipToLocationId ? 'none !important' : '' }, "&.Mui-focused .MuiIconButton-root": { color: 'rgba(0,0,0,.42)' }
                                            }}
                                            endAdornment={filterShipToLocationId ? (<IconButton sx={{ visibility: filterShipToLocationId ? 'visible' : 'hidden', padding: '0' }} onClick={() => { setFilterShipToLocationId(''); setPage(1) }}><ClearIcon /></IconButton>) : false}
                                        >
                                            {
                                                shipToLocations.length < 1 ?
                                                    <MenuItem value='nooptions' disabled sx={{ textDecoration: 'italic' }}>{t('no_options')}</MenuItem>
                                                    :
                                                    shipToLocations.map((item) => (
                                                        <MenuItem key={item.id} value={item.id}>{item.code} - {item.name}</MenuItem>
                                                    ))
                                            }
                                        </Select>
                                    </FormControl>
                                </div>

                                <div className='w-full'>
                                    <FormControl sx={{ width: 'inherit' }}>
                                        <InputLabel sx={{}}>{t('search_by_status')}</InputLabel>
                                        <Select
                                            value={filterQuoteStatus}
                                            onChange={e => { setFilterQuoteStatus(e.target.value); setPage(1) }}
                                            sx={{
                                                boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: 'none !important' }, background: '#F5F5F5', borderRadius: '6px',
                                                ".MuiSelect-iconOutlined": { display: filterQuoteStatus ? 'none !important' : '' }, "&.Mui-focused .MuiIconButton-root": { color: 'rgba(0,0,0,.42)' }
                                            }}
                                            endAdornment={filterQuoteStatus ? (<IconButton sx={{ visibility: filterQuoteStatus ? 'visible' : 'hidden', padding: '0' }} onClick={() => { setFilterQuoteStatus(''); setPage(1) }}><ClearIcon /></IconButton>) : false}
                                        >
                                            {quoteStatuses.map((status) => (
                                                <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>

                                <div className='w-full'>
                                    <FormControl sx={{ width: 'inherit' }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <MobileDatePicker
                                                label={t('search_by_quote_date')}
                                                format="YYYY-MM-DD"
                                                value={filterQuoteDate}
                                                sx={{
                                                    boxShadow: 'none',
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        border: 'none !important'
                                                    },
                                                    background: '#F5F5F5',
                                                    borderRadius: '6px',
                                                    "&.Mui-focused .MuiIconButton-root": {
                                                        color: 'rgba(0,0,0,.42)'
                                                    }
                                                }}
                                                onChange={(newValue) => { setFilterQuoteDate(newValue); setPage(1) }}
                                                slotProps={{
                                                    textField: {
                                                        InputLabelProps: {},
                                                        InputProps: {
                                                            endAdornment: filterQuoteDate ? (
                                                                <IconButton
                                                                    sx={{ padding: '0' }}
                                                                    onClick={(e) => { e.stopPropagation(); setFilterQuoteDate(null); setPage(1) }}
                                                                >
                                                                    <ClearIcon />
                                                                </IconButton>
                                                            ) : undefined
                                                        }
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </div>
                            </div>
                        </div>
                        <div>
                            <Table
                                items={quotes}
                                total={total}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                opc={setPage}
                                orppc={setRowsPerPage}
                                onQuoteDeleted={getQuotes}
                            />
                        </div>
                    </div>
                </div>

            </AppLayout>

        </>
    )
}

export default Quotes
